import React, { useMemo, useRef, useState } from 'react';
import { Box, ClickAwayListener, Collapse, Stack, Typography } from '@mui/material';
import classnames from 'classnames';
import { CrossIcon, PinIcon, UserIcon } from 'assets';
import { useTranslation } from 'react-i18next';
import { useIsGuestCall, useRoomRightSidebar, useScreenSize } from 'hooks';
import { useAppSelector } from 'store';
import { selectPatients } from 'store/patientQueue';
import { PatientList } from 'components';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './WaitingQueue.module.scss';

export function WaitingQueue(): JSX.Element {
  const { t, i18n } = useTranslation('providerRoom');
  const isGuestCall = useIsGuestCall();
  const patients = useAppSelector(selectPatients);
  const { isMobile, isDesktop } = useScreenSize();
  const { toggleWaitingQueue, hideSidebar } = useRoomRightSidebar();
  const isRtl = i18n.dir() === 'rtl';

  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [pin, setPin] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const isOpen: boolean = useMemo(() => {
    return open && !isMobile;
  }, [open, isMobile]);

  const isHover: boolean = useMemo(() => {
    return hover && !isMobile;
  }, [hover, isMobile]);

  const isPin: boolean = useMemo(() => {
    return pin && !isMobile;
  }, [pin, isMobile]);

  const widthVars = useMemo(() => {
    let extra = 0;
    let emptyTextWidth = 0;
    const emptyQueueLabel = isHipaaLink ? t('noClientsInQueue') : t('noGuestsInQueue');

    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = '700 14px "Open Sans", sans-serif';
        const measured = Math.ceil(ctx.measureText(t('join')).width);
        const desiredButtonWidth = measured + 20; // text + horizontal padding
        const baselineButtonWidth = 96;
        extra = Math.max(0, Math.min(140, desiredButtonWidth - baselineButtonWidth));

        // Match desktop empty-state list row so pinned queue doesn't clip localized text.
        ctx.font = '400 22px "Open Sans", sans-serif';
        emptyTextWidth = Math.ceil(ctx.measureText(emptyQueueLabel).width);
      }
    }

    const emptyBasedWidth = Math.max(200, emptyTextWidth + 56); // includes list/toggle paddings
    const pinnedWidth = patients.length === 0 ? emptyBasedWidth : 160 + extra;

    return {
      '--wq-min-width': `${Math.max(280 + extra, emptyBasedWidth)}px`,
      '--wq-pinned-width': `${pinnedWidth}px`,
      '--wq-actions-extra': `${extra}px`,
    } as React.CSSProperties;
  }, [patients.length, t, i18n.language, i18n.resolvedLanguage]);

  const handleToggle = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    // While pinned, do not allow collapsing by clicking the blue toggle area.
    // User must click the pin icon to unpin first.
    if (isPin) {
      return;
    }

    setOpen((prevOpen) => !prevOpen);

    if (isMobile) {
      toggleWaitingQueue();
    }
  };

  const handlePinToggle = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setPin((prevPin) => !prevPin);
  };

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const handleClose = () => {
    if (!isPin) {
      setOpen(false);
      setHover(false);
    }
  };

  const isActive = (isHover || isOpen) && !isPin;

  return (
    <Box className={classnames(styles.container, { [styles.containerMobile]: isMobile })}>
      <ClickAwayListener onClickAway={handleClose}>
        <Box
          className={styles.innerContainer}
          sx={{ background: isGuestCall ? '#FFF' : 'var(--app-surface)' }}
          style={widthVars}
        >
          {!isMobile && (
            <Box
              className={classnames(styles.desktopListContainer, {
                [styles.desktopListContainerOpen]: isOpen,
              })}
            >
              <Collapse in={isOpen} timeout='auto' unmountOnExit>
                <Box
                  className={classnames(styles.listContainer, {
                    [styles.listContainerEmpty]: patients.length === 0,
                  })}
                >
                  <PatientList isCallStarted isListPinned={isPin} />
                </Box>
              </Collapse>
            </Box>
          )}
          <Box
            ref={anchorRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleToggle}
            className={classnames(styles.toggleButtonContainer, {
              [styles.toggleButtonContainerActive]: isOpen,
              [styles.toggleButtonContainerPinned]: isPin,
              [styles.toggleButtonContainerHovered]: isHover,
              [styles.pointer]: !isOpen,
            })}
            sx={{ background: 'var(--app-brand-primary)', color: '#FFF' }}
          >
            {(isDesktop || !isActive) && (
              <Stack direction='row' alignItems='baseline' spacing='2px' sx={{ direction: 'ltr' }}>
                {isRtl ? (
                  <>
                    <Typography component='p' variant='body3' fontWeight={700} sx={{ color: '#FFF' }}>
                      {patients.length}
                    </Typography>
                    <UserIcon sx={{ color: '#FFF', fontSize: '24px' }} />
                  </>
                ) : (
                  <>
                    <UserIcon sx={{ color: '#FFF', fontSize: '24px' }} />
                    <Typography component='p' variant='body3' fontWeight={700} sx={{ color: '#FFF' }}>
                      {patients.length}
                    </Typography>
                  </>
                )}
              </Stack>
            )}
            {isActive && (
              <Typography component='p' textTransform='uppercase' variant='body4' sx={{ color: '#FFF' }}>
                {isHipaaLink ? t('clientQueue') : t('waitingRoom')}
              </Typography>
            )}
            {isHover && !isOpen && !!patients.length && (
              <Box
                sx={{
                  background: 'var(--app-error)',
                  borderRadius: '12px',
                  padding: '5px 7px',
                }}
                height={20}
              >
                <Typography
                  component='p'
                  textTransform='uppercase'
                  variant='body4'
                  lineHeight='0.9rem'
                  sx={{ color: '#FFF' }}
                >
                  {!!patients.length && patients.length}
                </Typography>
              </Box>
            )}
            {isOpen && (
              <Stack
                direction={isRtl ? 'row-reverse' : 'row'}
                alignItems='center'
                justifySelf='flex-end'
                sx={{ marginInlineStart: 'auto', gap: '20px' }}
              >
                <PinIcon
                  sx={{ color: '#FFF', fontSize: '16px' }}
                  className={classnames({
                    [styles.pointer]: isOpen,
                  })}
                  onClick={handlePinToggle}
                />
                {!isPin && (
                  <CrossIcon
                    sx={{ color: '#FFF', fontSize: '13px' }}
                    className={classnames({
                      [styles.pointer]: isOpen,
                    })}
                    onClick={(e) => {
                      handleToggle(e);
                      isPin && handlePinToggle(e);
                      handleMouseLeave();
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>

          {isMobile && (
            <Collapse in={isOpen} timeout='auto' unmountOnExit>
              <Box
                className={classnames(styles.listContainer, {
                  [styles.listContainerEmpty]: patients.length === 0,
                })}
              >
                <PatientList
                  isCallStarted
                  isListPinned={isPin}
                  onAfterStartCall={isMobile ? hideSidebar : undefined}
                  onAfterOpenChat={isMobile ? hideSidebar : undefined}
                />
              </Box>
            </Collapse>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
}
